import {StorageUtility} from './Storage';
import {URLManager} from './URLManager';
import nonUsSupportedRegions, {
    nonUsSupportedRegionsArr,
    nonUsSupportedRegionsSet,
    nonUsSupportedTopLevelDomains,
} from '../data/non-us-supported-regions';

declare var window: any;
declare var $: any;

export class Locale {
    private static instance: Locale;

    static getInstance(): Locale {
        if (!Locale.instance) {
            Locale.instance = new Locale();
        }
        return Locale.instance;
    }

    public phoneCodes = require('./../data/country-code-to-phone.json');
    public countryCodes = require('./../data/country-to-country-code.json');
    public countries = require('./../data/country-code-to-country.json');

    public getCurrentLocale() {
        if (window.location.pathname) {
            const locale = window.location.pathname.substr(1, 5);
            if (locale[2] === '_') return locale;
            return 'en_US';
        } else {
            return 'en_US';
        }
    }

    public getLocalizedBlogPathFor(blogPath, fallbackUrl = false) {
        const localePaths = {
            ar_AE: 'ar',
            de_DE: 'de',
            es_ES: 'es',
            fr_FR: 'fr',
            it_IT: 'it',
            ja_JP: 'ja',
            nl_NL: 'nl',
            pl_PL: 'pl',
            pt_BR: 'pt-br',
            ru_RU: 'ru',
            zh_CN: 'zh-hans',
        };

        const currentLocale = this.getCurrentLocale();
        if (currentLocale == 'en_US' || currentLocale == 'en_GB' || fallbackUrl == true) {
            return '/blog' + blogPath;
        } else {
            return '/blog/' + (localePaths[currentLocale] || '') + blogPath;
        }
    }

    /**
     * Get country from local storage item: original_keeper_region.
     * @returns {string} The full country name.
     */
    public getCountryFromStorage() {
        const countryMap = require('./../data/country-code-to-country.json');
        // Use the same logic as getCurrentCountry() to handle QA testing properties
        const countryCode = (
            (StorageUtility.getInstance().getLocal('qa_region_tester') &&
                StorageUtility.getInstance().getLocal('qa_country')) ||
            StorageUtility.getInstance().getLocal('original_keeper_region') ||
            ''
        ).toUpperCase();
        return countryMap && countryMap[countryCode] ? countryMap[countryCode] : null;
    }

    /**
     * Get country from current locale.
     * @returns {string} The full country name.
     */
    public getCountryFromlocale() {
        const countryLocaleMap = require('./../data/locale-to-country.json');
        const locale = this.getCurrentLocale();
        return countryLocaleMap && countryLocaleMap[locale]
            ? countryLocaleMap[locale]
            : 'United States';
    }

    /**
     * Get country from storage (original_keeper_region). If that doesn't exist,
     * get the country from the locale. If that doesn't exist, default to United States.
     * @returns {string} The full country name.
     */
    public getCountryFromStorageOrLocale() {
        const countryfromStorage = this.getCountryFromStorage();
        const countryfromLocale = this.getCountryFromlocale();
        return countryfromStorage
            ? countryfromStorage
            : countryfromLocale
            ? countryfromLocale
            : 'United States';
    }

    /**
     * Get country from original_keeper_region and return boolean for whether it
     * matches the provided region.
     * @param {(string|string[])} region - The region(s) to test.
     * @returns {boolean} Boolean describing whether the original_keeper_region
     * matches the provided region.
     */
    public isOriginalKeeperRegion(region: string | string[]) {
        let regions: Array<string>;
        let countries: Set<string>;

        if (typeof region === 'string') {
            region = [region];
        }
        regions = [...region];

        if (regions.indexOf('EU') !== -1) {
            regions = [...regions, ...require('./../data/eu-country-codes.json')];
        }

        countries = new Set(regions);

        return countries.has(
            (
                (StorageUtility.getInstance().getLocal('qa_region_tester') &&
                    StorageUtility.getInstance().getLocal('qa_country')) ||
                StorageUtility.getInstance().getLocal('original_keeper_region') ||
                ''
            ).toUpperCase()
        );
    }

    public getLanguageCodeFromLocale() {
        const locale = this.getCurrentLocale();
        return locale.substr(0, 2);
    }

    public getCountryCodeFromLocale() {
        const locale = this.getCurrentLocale();
        return locale.substr(3, 2);
    }

    /**
     * @summary Gets the effective country code with QA testing overrides applied.
     * @returns {string} The country code to use for region-based logic (currency, pricing, etc.).
     * @since 17.5.14
     */
    public getCurrentCountry(): string {
        // Use the same logic as isOriginalKeeperRegion but return the country code
        return (
            (StorageUtility.getInstance().getLocal('qa_region_tester') &&
                StorageUtility.getInstance().getLocal('qa_country')) ||
            StorageUtility.getInstance().getLocal('original_keeper_region') ||
            ''
        ).toUpperCase();
    }

    public embedCountryFlags(countrySelect) {
        const options = $(countrySelect).siblings('.fs-dropdown-options');
        $(options)
            .children()
            .each((i, item) => {
                const value = $(item).data('value');
                const countryCode = this.countryCodes[value];
                if (countryCode) {
                    const flagIcon =
                        "<span class='flagstrap-icon flagstrap-" +
                        countryCode.toLowerCase() +
                        "'></span>";
                    $(item).prepend(flagIcon);
                }
            });
    }

    public embedCountryPhoneExtensions(countrySelect) {
        try {
            const options = $(countrySelect).siblings('.fs-dropdown-options');
            $(options)
                .children()
                .each((i, item) => {
                    const value = $(item).data('value');
                    const phoneCode = this.countryToPhone(value);
                    $(item).append("<span class='country-phone'>(+" + phoneCode + ')</span>');
                });
        } catch (err) {
            console.warn(err);
        }
    }

    public countryToPhone(country) {
        try {
            const countryCode = this.countryToCode(country);
            return this.phoneCodes[countryCode];
        } catch (err) {
            console.warn(err);
        }
    }

    public countryToCode(country) {
        try {
            return this.countryCodes[country];
        } catch (err) {
            console.warn(err);
        }
    }

    public dropdownAutoSelectCountry(select) {
        const country = this.getCountryFromStorageOrLocale();

        if (country) {
            $(select).val(country).trigger('change');
        } else {
            $(select).val('United States').trigger('change');
        }
    }

    public dropdownAutoSelectTimeZone(select) {
        // TODO: Autoselect user's local time zone
        $(select).val('GMT').trigger('change');
        // const offset = new Date().getTimezoneOffset() / 60;

        // if (offset) {
        //     $(select).val(offset).trigger("change");
        // }
        // else {
        //     $(select).val(0).trigger("change");
        // }
    }

    /**
     * @summary Takes an endpoint and replaces the top level domain based on the region.
     * @param {string} region - The region code (e.g. US, EU, AU)
     * @param {string} endpoint - The endpoint url.
     * @since 16.10.14
     */
    public convertEndpointForRegion(region: string, endpoint: string): string {
        // Convert endpoint to .com first.
        nonUsSupportedTopLevelDomains.forEach(tld => (endpoint = endpoint.replace(tld, '.com')));

        // Loop through non us supported regions to find matching region.
        for (let i = 0; i < nonUsSupportedRegionsArr.length; ++i) {
            const r = nonUsSupportedRegionsArr[i].toUpperCase();
            if (region.toUpperCase() === r) {
                endpoint = endpoint.replace('.com', nonUsSupportedRegions[r]);
                return endpoint;
            }
        }

        return null;
    }

    /**
     * @summary Checks if user belongs to a data region.
     * @returns {boolean} If the user belongs to data region.
     * @since 16.10.14
     */
    public isDataRegion = (dataRegion: string, select = '', $scope = null) => {
        if ($scope && $scope[dataRegion.toLowerCase()] === true) return true;
        if (dataRegion.toLowerCase() === 'eu') {
            if (
                this.hasDataRegionParam('eu') ||
                this.isSelectedCountryEU(select) ||
                this.isOriginalKeeperRegion('EU')
            )
                return true;
        } else {
            if (
                this.hasDataRegionParam(dataRegion.toLowerCase()) ||
                this.isSelectedCountry(select, this.countries[dataRegion.toUpperCase()]) ||
                this.isOriginalKeeperRegion(dataRegion.toUpperCase())
            ) {
                return true;
            }
        }
        return false;
    };

    /**
     * @summary Checks the selected locale for EU.
     * @returns {boolean} Whether or not the selected option was EU.
     * @since 12.0.0
     */
    public isLocaleEU = () => {
        const locale = this.getCurrentLocale();
        switch (locale) {
            case 'en_US':
                return false;
                break;
            case 'es_ES':
                return true;
                break; // eu
            case 'ja_JP':
                return false;
                break;
            case 'pt_BR':
                return false;
                break;
            case 'nl_NL':
                return true;
                break; // eu
            case 'de_DE':
                return true;
                break; // eu
            case 'ru_RU':
                return false;
                break;
            case 'pl_PL':
                return true;
                break; // eu
            case 'zh_CN':
                return false;
                break;
            case 'en_GB':
                return true;
                break; // eu
            case 'it_IT':
                return true;
                break; // eu
            case 'ar_AE':
                return false;
                break;
            case 'fr_FR':
                return true;
                break; // eu
        }
    };

    /**
     * @summary Checks for data region parameter in the URL.
     * @param {string} region - The region code.
     * @returns {boolean} Whether or not data region matches region arg.
     * @since 16.10.14
     */
    public hasDataRegionParam = (region: string) => {
        return URLManager.getInstance().getParams()['data_region'] === region.toLowerCase();
    };

    /**
     * @summary Grabs the selected dropdown option for EU checking.
     * @param {string} select - The dropdown to compare against.
     * @returns {boolean} Whether or not the selected option was EU.
     * @since 12.0.0
     */
    public isSelectedCountryEU(select) {
        const country = $(select);
        if (country.length < 1) return false;
        if (country.find(':selected').data('region') === 'eu') {
            return true;
        }
    }

    /**
     * @summary Grabs the selected dropdown option for country checking.
     * @param {string} select - The dropdown to compare against.
     * @param {string} countryName - Two letter country code.
     * @returns {boolean} Whether or not the selected option matches countryName argument.
     * @since 16.10.14
     */
    public isSelectedCountry(select, countryName: string): boolean {
        const country = $(select);
        if (country.length < 1) return false;
        const countryValue = country.find(':selected').text();
        if (countryValue && countryValue.toLowerCase() === countryName.toLowerCase()) {
            return true;
        }
    }

    public setUserRegionSession(user_region) {
        StorageUtility.getInstance().setSession('keeper_user_region', user_region.toLowerCase());
    }

    /**
     * @summary Updates the language displayed in the footer.
     * @since 12.0.0
     */
    public updateDisplayedLanguage = () => {
        const locale = this.getCurrentLocale();
        switch (locale) {
            case 'en_US':
                $('.current_locale').html('English (US)');
                break;
            case 'es_ES':
                $('.current_locale').html('Español (ES)');
                break;
            case 'ja_JP':
                $('.current_locale').html('日本語 (JP)');
                break;
            case 'pt_BR':
                $('.current_locale').html('Português (BR)');
                break;
            case 'nl_NL':
                $('.current_locale').html('Nederlands (NL)');
                break;
            case 'de_DE':
                $('.current_locale').html('Deutsch (DE)');
                break;
            case 'ru_RU':
                $('.current_locale').html('Pусский (RU)');
                break;
            case 'pl_PL':
                $('.current_locale').html('Polski (PL)');
                break;
            case 'zh_CN':
                $('.current_locale').html('中文 (CN)');
                break;
            case 'en_GB':
                $('.current_locale').html('English (UK)');
                break;
            case 'it_IT':
                $('.current_locale').html('Italiano (IT)');
                break;
            case 'pt_PT':
                $('.current_locale').html('Português (PT)');
                break;
            case 'ar_AE':
                $('.current_locale').html('العربية (AE)');
                break;
            case 'fr_FR':
                $('.current_locale').html('Français (FR)');
                break;
        }
    };

    public getUserCountryFromHeadersForStudentDiscount = (callback, callback2) => {
        var req = new XMLHttpRequest();
        req.open('HEAD', window.keeperPreload.apiEndpoint + '/bi_api/v1/getUserCountry', true);

        req.timeout = 1000; // time in milliseconds

        req.ontimeout = function (e) {
            callback2();
        };

        req.onload = function (e) {
            var isUSofA = false;
            if (req.readyState === 4) {
                if (req.status === 200) {
                    if (
                        localStorage &&
                        (localStorage['keeper_override_region'] === 'true' ||
                            localStorage['keeper_override_region'] === true)
                    ) {
                        //console.log("KEEPER EU OVERRIDE DETECTED!! WELCOME TO EU REGION!");
                    } else {
                        var headers: any = req.getAllResponseHeaders().toLowerCase();
                        headers = headers.split('\n');
                        var xCountry = '';
                        var xState = '';
                        for (var i = 0; i < headers.length; i++) {
                            if (headers[i].length > 1) {
                                var line = headers[i].split(':', 2);
                                line[0] = line[0].replace(' ', '');
                                line[1] = line[1].replace(' ', '');

                                if (line[0].indexOf('x-country-code') !== -1) {
                                    if (
                                        window.localStorage &&
                                        window.localStorage.getItem('qa_eu_tester') === 'true'
                                    ) {
                                        line[1] = 'IE';
                                    }

                                    if (
                                        window.localStorage &&
                                        window.localStorage.getItem('qa_region_tester') === 'true'
                                    ) {
                                        const xCountryOverride = window.localStorage.getItem(
                                            'qa_country'
                                        );

                                        if (xCountryOverride) {
                                            line[1] = xCountryOverride;
                                        }
                                    }

                                    xCountry = line[1];
                                } else if (line[0].indexOf('x-state-code') !== -1) {
                                    if (
                                        window.localStorage &&
                                        window.localStorage.getItem('qa_region_tester') === 'true'
                                    ) {
                                        const xStateOverride = window.localStorage.getItem(
                                            'qa_state'
                                        );

                                        if (xStateOverride) {
                                            line[1] = xStateOverride;
                                        }
                                    }

                                    xState = line[1];
                                }
                            }
                        }
                        if (
                            xCountry &&
                            xCountry.trim() === 'us' &&
                            (!xState || xState.trim().toLowerCase() !== 'california')
                        ) {
                            isUSofA = true;
                            callback();
                        }
                    }
                }
            }
            if (!isUSofA && callback2) {
                callback2();
            }
        };

        req.onerror = function (e) {
            console.error(req.statusText);
            if (callback2) {
                callback2();
            }
        };

        req.send(null);
    };

    /**
     * @summary Orders name fields based on locale, with special handling for Japanese order
     * @since 17.5.4
     * @param fields Array of form field components to order
     * @param locale Current locale string
     * @returns Array of ordered fields
     */
    public localizeNameOrder<T>(fields: T[]): T[] {
        const locale = this.getCurrentLocale();
        return fields.slice()[locale === 'ja_JP' ? 'reverse' : 'slice']();
    }

    /**
     * @summary Gets a formatted Console link with EU & email embedded.
     * @param {object} options - The parameters to pass (e.g. email & datacenter)
     * @param {string} region - The region code (e.g. US, EU, AU)
     * @since 12.0.0
     */
    public getConsoleAnchor(options, region?: string) {
        let href = window.keeperPreload.apiEndpoint;
        const dataRegion = URLManager.getInstance().getParams()['data_region'];

        if (options.region) {
            region = options.region.toUpperCase();
        }

        //because we are removing the country from the form we are overriding the logic here
        if (
            !(dataRegion && nonUsSupportedRegionsArr.includes(dataRegion.toUpperCase())) &&
            this.isOriginalKeeperRegion(nonUsSupportedRegionsArr)
        ) {
            if (this.isOriginalKeeperRegion('EU')) {
                region = 'EU';
            } else {
                region = StorageUtility.getInstance().getLocal('original_keeper_region');
            }
        }

        if (region) {
            region = region.toUpperCase();
        } else {
            for (let i = 0; i < nonUsSupportedRegionsArr.length; ++i) {
                const r = nonUsSupportedRegionsArr[i];
                if (this.isDataRegion(r.toUpperCase())) {
                    region = r.toUpperCase();
                }
            }
            if (!region && this.isLocaleEU()) {
                region = 'EU';
            }
        }

        if (!region) {
            region = 'US';
        }

        if (region !== 'US' && nonUsSupportedRegionsSet.has(region)) {
            href = this.convertEndpointForRegion(region, href);
        }

        if (href[href.length - 1] !== '/') {
            href = href + '/';
        }

        if (options.login) {
            href = href + this.getCurrentLocale() + '/console/?#login';
        }

        if (options.signup) {
            href = href + this.getCurrentLocale() + '/console/';
            const validKeys = new Set(['email', 'name', 'company', 'isMSP']);
            let params = '';
            for (let key in options) {
                if (validKeys.has(key) && typeof options[key] !== 'undefined') {
                    params += `/${key}/${encodeURIComponent(options[key])}`;
                }
            }
            href += `#trial${params}`;
        }

        return href;
    }

    /**
     * @summary Gets a formatted Console link with EU & email embedded.
     * @param {object} options - The parameters to pass (e.g. email & datacenter)
     * @param {string} region - The region code (e.g. US, EU, AU)
     * @since 12.0.0
     */
    public getVaultAnchor(options, region?: string) {
        let href = window.keeperPreload.apiEndpoint;
        const dataRegion = URLManager.getInstance().getParams()['data_region'];

        if (options.region) {
            region = options.region.toUpperCase();
        }

        //because we are removing the country from the form we are overriding the logic here
        if (
            !(dataRegion && nonUsSupportedRegionsArr.includes(dataRegion.toUpperCase())) &&
            this.isOriginalKeeperRegion(nonUsSupportedRegionsArr)
        ) {
            if (this.isOriginalKeeperRegion('EU')) {
                region = 'EU';
            } else {
                region = StorageUtility.getInstance().getLocal('original_keeper_region');
            }
        }

        if (region) {
            region = region.toUpperCase();
        } else {
            for (let i = 0; i < nonUsSupportedRegionsArr.length; ++i) {
                const r = nonUsSupportedRegionsArr[i];
                if (this.isDataRegion(r.toUpperCase())) {
                    region = r.toUpperCase();
                }
            }
            if (!region && this.isLocaleEU()) {
                region = 'EU';
            }
        }

        if (!region) {
            region = 'US';
        }

        if (region !== 'US' && nonUsSupportedRegionsSet.has(region)) {
            href = this.convertEndpointForRegion(region, href);
        }

        if (href[href.length - 1] !== '/') {
            href = href + '/';
        }

        if (options.login) {
            href = href + 'vault/#/lang/' + this.getCurrentLocale();
        }

        if (options.signup) {
            href = href + 'vault/#new/';
            if (options.email) {
                href =
                    href +
                    'email/' +
                    encodeURIComponent(options.email) +
                    '/lang/' +
                    this.getCurrentLocale();
            } else {
                href = href + 'lang/' + this.getCurrentLocale();
            }
        }

        return href;
    }

    /**
     * @summary Replaces login and signup links to handle EU.
     * @since 12.0.0
     */
    public replaceOutboundAnchors() {
        $('[data-vault-login]').attr(
            'href',
            this.getVaultAnchor({
                login: true,
            })
        );

        $('[data-vault-signup]').attr(
            'href',
            this.getVaultAnchor({
                signup: true,
            })
        );

        $('[data-console-login]').attr(
            'href',
            this.getConsoleAnchor({
                login: true,
            })
        );
    }

    /**
     * @summary Redirect user to corresponding thank you page.
     * @since 12.0.0
     * @param {string} page - The page key to compare against.
     * @param {array} parameters - The parameters to pass to the thankyou page.
     */
    public thankyou = (page, params = [], region = 'US') => {
        let href = window.keeperPreload.apiEndpoint;

        if (href[href.length - 1] !== '/') {
            href = href + '/';
        }

        switch (page) {
            case 'contact':
                href = href + 'thank-you.html';
                break;

            case 'free-trial':
                href = href + 'trial/thankyou-freetrial/';
                break;
        }

        if (region.toUpperCase() !== 'US' && nonUsSupportedRegionsSet.has(region.toUpperCase())) {
            href = `${href}?data_region=${region.toLowerCase()}`;
        } else {
            // Try to find the proper data region.
            for (let i = 0; i < nonUsSupportedRegionsArr.length; ++i) {
                const r = nonUsSupportedRegionsArr[i];
                if (Locale.getInstance().isDataRegion(r.toUpperCase())) {
                    region = r.toUpperCase();
                }
            }

            // If found and is non US, update the href.
            if (nonUsSupportedRegionsSet.has(region.toUpperCase())) {
                href = `${href}?data_region=${region.toLowerCase()}`;
            }
        }

        if (params.length > 0) {
            params.forEach((value, index) => {
                if (href.indexOf('?') !== -1) {
                    href = href + '&' + value;
                } else {
                    href = href + '?' + value;
                }
            });
        }

        window.location.href = href;
    };

    /**
     * @summary Calls the material design component handler to refresh material design elements.
     * @since 16.0.0
     */

    public cleanRequest(request) {
        nonUsSupportedRegionsArr.forEach(regionCode => {
            regionCode = regionCode.toUpperCase();
            if (request[`isCountry${regionCode}`] !== undefined) {
                delete request[`isCountry${regionCode}`];
            }

            if (request[`isEmail${regionCode}`] !== undefined) {
                delete request[`isEmail${regionCode}`];
            }
        });

        if (request.isExistingEmail !== undefined) {
            delete request.isExistingEmail;
        }

        return request;
    }
}

export default Locale;
